import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  isNativeApp,
  hideSplashScreen,
  setStatusBarTheme,
  configureKeyboard,
  setupBackButton,
  setupDeepLinks,
  setupNetworkListener,
  setupPushNotifications,
  triggerHaptic,
  setupOrientationListeners,
  lockPortrait,
  setupExternalLinkInterceptor,
} from '../utils/nativeBridge';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import { IS_MESSENGER } from '../config/appVariant';
import { App } from '@capacitor/app';

/**
 * Handles all native mobile lifecycle events, status bar theming,
 * virtual keyboard configuration, deep linking, and Android hardware back button.
 */
const NativeMobileBridge = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const { user } = useAuth();
  const lastBackPressRef = useRef(0);
  const currentPathRef = useRef(location.pathname);

  // Keep currentPathRef up to date and enforce portrait orientation on route change
  useEffect(() => {
    currentPathRef.current = location.pathname;
    lockPortrait();
  }, [location.pathname]);

  // 1. Core Native lifecycle, deep links, keyboard, back button, network, orientation
  useEffect(() => {
    // Setup global fullscreen orientation listeners (works on both native and web)
    const cleanupOrientation = setupOrientationListeners();

    // Intercept external web URLs (prevents WebView takeover on iOS/Android)
    const cleanupExternalLinks = setupExternalLinkInterceptor();

    if (!isNativeApp()) {
      return () => {
        cleanupOrientation();
        cleanupExternalLinks();
      };
    }

    // Hide splash screen gracefully once hydrated
    hideSplashScreen();

    // Configure keyboard resize mode
    configureKeyboard();

    // Status bar theme synchronization
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    setStatusBarTheme(mediaQuery.matches);

    const handleThemeChange = (e) => {
      setStatusBarTheme(e.matches);
    };

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleThemeChange);
    } else if (mediaQuery.addListener) {
      mediaQuery.addListener(handleThemeChange);
    }

    // Handle Deep Links (Universal Links & custom schemes)
    const cleanupDeepLinks = setupDeepLinks((path) => {
      if (path) {
        console.log('[NativeBridge] Deep link received -> navigating to:', path);
        // Small delay ensures router and views are fully mounted
        setTimeout(() => {
          navigate(path);
        }, 80);
      }
    });

    // Handle Android Hardware Back Button
    const cleanupBack = setupBackButton(() => {
      // Priority 1: If any modal or dialog is open, close it
      const openDialog = document.querySelector('[role="dialog"]');
      if (openDialog) {
        const closeBtn = openDialog.querySelector('button[aria-label="Close"], button[title="Close"]');
        if (closeBtn) {
          closeBtn.click();
          triggerHaptic('light');
          return true;
        }
        const escEvent = new KeyboardEvent('keydown', { key: 'Escape', code: 'Escape', bubbles: true });
        openDialog.dispatchEvent(escEvent);
        triggerHaptic('light');
        return true;
      }

      // Priority 2: If on root page (Home or Explore), require double-tap to exit
      const currentPath = currentPathRef.current || '/';
      const isRoot =
        currentPath === '/home' ||
        currentPath === '/explore' ||
        currentPath === '/' ||
        (IS_MESSENGER && currentPath === '/messages');

      if (isRoot) {
        const now = Date.now();
        if (now - lastBackPressRef.current < 2000) {
          App.exitApp();
        } else {
          lastBackPressRef.current = now;
          toast.info('Press back again to exit');
          triggerHaptic('light');
        }
        return true;
      }

      // Priority 3: Navigate back in history
      navigate(-1);
      triggerHaptic('selection');
      return true;
    });

    // Monitor network status
    const cleanupNetwork = setupNetworkListener((isConnected) => {
      if (!isConnected) {
        toast.warning('No internet connection');
        triggerHaptic('warning');
      }
    });

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleThemeChange);
      } else if (mediaQuery.removeListener) {
        mediaQuery.removeListener(handleThemeChange);
      }
      cleanupDeepLinks();
      cleanupBack();
      cleanupNetwork();
      cleanupOrientation();
      cleanupExternalLinks();
    };
  }, [navigate, toast]);

  // 2. Setup Native Push Notifications (isolated to user auth state)
  useEffect(() => {
    if (!isNativeApp() || !user) return;

    const cleanupPush = setupPushNotifications((targetPath) => {
      if (targetPath) navigate(targetPath);
    });

    return () => {
      if (typeof cleanupPush === 'function') cleanupPush();
    };
  }, [user, navigate]);

  return null;
};

export default NativeMobileBridge;
